import { useEffect, useMemo, useState } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { AlertCircle, CalendarDays, Inbox, Music2, RefreshCw, Search, Wrench, X } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { artistsApi, producersApi, providersApi } from "./api";
import type {
  ArtistApplication,
  ListFilters,
  PaginatedResponse,
  ProducerApplication,
  ServiceProviderApplication,
} from "./types";
import { StatusBadge } from "./shared/StatusBadge";
import { formatApplicantName, formatCountry, formatDate } from "./shared/format";
import { ProducerDrawer } from "./ProducersScreen";
import { ArtistDrawer } from "./ArtistsScreen";
import { ProviderDrawer } from "./ProvidersScreen";
import { LoadingBlock } from "../dashboard/shared/StateBlocks";
import { useSession } from "@/lib/auth/session";
import { PERMISSIONS } from "@/lib/permissions/catalog";
import { useDebounce } from "@/lib/utils/useDebounce";
import { cn } from "@/lib/utils/cn";

/**
 * Bandeja unificada de postulaciones (productor · artista · proveedor).
 *
 * Desde la puerta única del portal (`mixcore.club/unete`) una misma persona
 * puede postular a varios perfiles a la vez, y cada perfil genera su propia
 * postulación. Esta bandeja las junta agrupadas por PERSONA para revisarlas
 * de una pasada.
 *
 * No hay endpoint nuevo: consume los 3 listados admin de siempre y mezcla en
 * el cliente. Al abrir una postulación se usa el MISMO drawer de su pestaña
 * (aprobar / rechazar / notas idénticos). Las query keys cuelgan de
 * ["applications", <tipo>] para que las invalidaciones de esos drawers
 * refresquen también la bandeja.
 *
 * Límite: 100 por tipo y estado (máximo del backend). Si hay más, se avisa y
 * se remite a la pestaña de cada tipo.
 */

type Kind = "producer" | "artist" | "provider";
type View = "review" | "approved" | "rejected" | "all";

const KIND_ORDER: Kind[] = ["producer", "artist", "provider"];

const KINDS: Record<Kind, { label: string; plural: string; icon: LucideIcon; perm: string; cacheKey: string }> = {
  producer: { label: "Productor", plural: "Productores", icon: CalendarDays, perm: PERMISSIONS.APPS_PRODUCERS_VIEW, cacheKey: "producers" },
  artist: { label: "Artista", plural: "Artistas", icon: Music2, perm: PERMISSIONS.APPS_ARTISTS_VIEW, cacheKey: "artists" },
  provider: { label: "Proveedor", plural: "Proveedores", icon: Wrench, perm: PERMISSIONS.APPS_PROVIDERS_VIEW, cacheKey: "providers" },
};

// Estados que pide cada vista, por tipo (el proveedor aprobado es verified/featured).
const VIEW_STATUSES: Record<View, Record<Kind, Array<string | undefined>>> = {
  review: { producer: ["pending", "contacted"], artist: ["pending", "contacted"], provider: ["pending", "contacted"] },
  approved: { producer: ["approved"], artist: ["approved"], provider: ["verified", "featured"] },
  rejected: { producer: ["rejected"], artist: ["rejected"], provider: ["rejected"] },
  all: { producer: [undefined], artist: [undefined], provider: [undefined] },
};

const VIEWS: Array<{ value: View; label: string }> = [
  { value: "review", label: "Por revisar" },
  { value: "approved", label: "Aprobadas" },
  { value: "rejected", label: "Rechazadas" },
  { value: "all", label: "Todas" },
];

const PAGE_SIZE = 100; // máximo que aceptan los listados admin

type AnyApplication = ProducerApplication | ArtistApplication | ServiceProviderApplication;

interface InboxItem {
  kind: Kind;
  id: number;
  status: string;
  createdAt: string;
  title: string; // organización / nombre artístico / nombre comercial
  location: string;
  userId: number | null;
  personName: string;
  personEmail: string;
}

interface PersonGroup {
  key: string;
  name: string;
  email: string;
  items: InboxItem[];
  latestAt: string;
}

function listFor(kind: Kind, filters: ListFilters): Promise<PaginatedResponse<AnyApplication>> {
  if (kind === "producer") return producersApi.list(filters);
  if (kind === "artist") return artistsApi.list(filters);
  return providersApi.list(filters);
}

function toItem(kind: Kind, row: AnyApplication): InboxItem {
  const location = [row.city, formatCountry(row.country)].filter(Boolean).join(", ");
  if (kind === "provider") {
    const p = row as ServiceProviderApplication;
    return {
      kind, id: p.id, status: p.status, createdAt: p.createdAt, title: p.businessName, location,
      userId: p.submittedByUserId ?? null,
      personName: p.submittedBy ? formatApplicantName(p.submittedBy) : p.contactName || "—",
      personEmail: (p.submittedBy?.email || p.contactEmail || "").toLowerCase(),
    };
  }
  const a = row as ProducerApplication | ArtistApplication;
  return {
    kind, id: a.id, status: a.status, createdAt: a.createdAt,
    title: kind === "producer" ? (a as ProducerApplication).organizationName : (a as ArtistApplication).artistName,
    location,
    userId: a.userId ?? null,
    personName: formatApplicantName(a.applicant),
    personEmail: (a.applicant?.email || "").toLowerCase(),
  };
}

/** Agrupa por persona: por userId, o por email si la postulación no trae usuario. */
function groupByPerson(items: InboxItem[]): PersonGroup[] {
  const emailToUser = new Map<string, number>();
  items.forEach((it) => {
    if (it.userId && it.personEmail) emailToUser.set(it.personEmail, it.userId);
  });
  const groups = new Map<string, PersonGroup>();
  items.forEach((it) => {
    const uid = it.userId ?? (it.personEmail ? emailToUser.get(it.personEmail) : undefined);
    const key = uid ? `u:${uid}` : `e:${it.personEmail || `${it.kind}-${it.id}`}`;
    const g = groups.get(key);
    if (!g) {
      groups.set(key, { key, name: it.personName, email: it.personEmail, items: [it], latestAt: it.createdAt });
      return;
    }
    g.items.push(it);
    if (it.createdAt > g.latestAt) g.latestAt = it.createdAt;
    if ((!g.name || g.name === "—") && it.personName) g.name = it.personName;
    if (!g.email && it.personEmail) g.email = it.personEmail;
  });
  const out = Array.from(groups.values());
  out.forEach((g) => g.items.sort((a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind)));
  return out.sort((a, b) => (a.latestAt < b.latestAt ? 1 : -1));
}

export function InboxScreen() {
  const hasPermission = useSession((s) => s.hasPermission);
  const allowedKinds = KIND_ORDER.filter((k) => hasPermission(KINDS[k].perm));

  const [view, setView] = useState<View>("review");
  const [kindFilter, setKindFilter] = useState<Kind | "">("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<{ kind: Kind; id: number } | null>(null);
  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    setSelected(null);
  }, [view]);

  const kinds = kindFilter ? allowedKinds.filter((k) => k === kindFilter) : allowedKinds;
  const requests = kinds.flatMap((kind) =>
    VIEW_STATUSES[view][kind].map((status) => ({ kind, status })),
  );

  const results = useQueries({
    queries: requests.map(({ kind, status }) => ({
      queryKey: ["applications", KINDS[kind].cacheKey, "inbox", status ?? "all", debouncedSearch],
      queryFn: () =>
        listFor(kind, { status, q: debouncedSearch || undefined, page: 1, pageSize: PAGE_SIZE }),
      placeholderData: (prev: PaginatedResponse<AnyApplication> | undefined) => prev,
    })),
  });

  const loading = results.length > 0 && results.every((r) => r.isLoading);
  const fetching = results.some((r) => r.isFetching);
  const failedKinds = Array.from(
    new Set(requests.filter((_, i) => results[i]?.isError).map((r) => r.kind)),
  );
  const truncated = results.some(
    (r) => r.data && r.data.total > (r.data.applications?.length ?? 0),
  );

  const items = useMemo(() => {
    const seen = new Set<string>();
    const out: InboxItem[] = [];
    results.forEach((r, i) => {
      const kind = requests[i]?.kind;
      if (!kind || !r.data) return;
      (r.data.applications ?? []).forEach((row) => {
        const k = `${kind}:${row.id}`;
        if (seen.has(k)) return;
        seen.add(k);
        out.push(toItem(kind, row));
      });
    });
    return out;
    // `results` cambia de identidad en cada render; los datos se comparan por dataUpdatedAt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results.map((r) => r.dataUpdatedAt).join("|"), requests.map((r) => `${r.kind}:${r.status}`).join("|")]);

  const groups = useMemo(() => groupByPerson(items), [items]);
  const multiProfile = groups.filter((g) => new Set(g.items.map((i) => i.kind)).size > 1).length;
  const countByKind = (k: Kind) => items.filter((i) => i.kind === k).length;

  // Detalle: mismas query keys que las pestañas de cada tipo (caché compartida).
  const selectedId = (k: Kind) => (selected?.kind === k ? selected.id : null);
  const producerDetail = useQuery({
    queryKey: ["applications", "producers", "detail", selectedId("producer")],
    queryFn: () => producersApi.detail(selectedId("producer")!),
    enabled: selectedId("producer") !== null,
  });
  const artistDetail = useQuery({
    queryKey: ["applications", "artists", "detail", selectedId("artist")],
    queryFn: () => artistsApi.detail(selectedId("artist")!),
    enabled: selectedId("artist") !== null,
  });
  const providerDetail = useQuery({
    queryKey: ["applications", "providers", "detail", selectedId("provider")],
    queryFn: () => providersApi.detail(selectedId("provider")!),
    enabled: selectedId("provider") !== null,
  });

  const refetchAll = () => results.forEach((r) => r.refetch());

  return (
    <div className="flex flex-col gap-4 animate-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="nx-eyebrow mb-1">Aplicaciones · Bandeja</p>
          <h1 className="nx-h1">Bandeja de postulaciones</h1>
          <p className="text-sm text-fg-muted mt-1 max-w-2xl">
            Productores, artistas y proveedores en un solo lugar, agrupados por persona.
            Quien se suma con varios perfiles aparece una vez: revisa y aprueba cada
            perfil por separado.
          </p>
        </div>
        <button onClick={refetchAll} className="nx-btn-secondary" disabled={fetching}>
          <RefreshCw size={14} className={fetching ? "animate-spin" : ""} />
          Actualizar
        </button>
      </header>

      {/* Vista por estado */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border border-border p-0.5 bg-bg-surface" role="tablist" aria-label="Estado">
          {VIEWS.map((v) => (
            <button
              key={v.value}
              role="tab"
              aria-selected={view === v.value}
              onClick={() => setView(v.value)}
              className={cn(
                "px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
                view === v.value ? "bg-accent text-fg-inverted" : "text-fg-muted hover:text-fg hover:bg-bg-hover",
              )}
            >
              {v.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <KindChip active={kindFilter === ""} onClick={() => setKindFilter("")} label="Todos" count={items.length} />
          {allowedKinds.map((k) => (
            <KindChip
              key={k}
              active={kindFilter === k}
              onClick={() => setKindFilter(kindFilter === k ? "" : k)}
              label={KINDS[k].plural}
              icon={KINDS[k].icon}
              count={kindFilter === "" || kindFilter === k ? countByKind(k) : undefined}
            />
          ))}
        </div>

        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-subtle pointer-events-none" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, ciudad o email…"
            className="nx-input pl-8 pr-8"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-fg-subtle hover:text-fg"
              aria-label="Limpiar búsqueda"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {allowedKinds.length === 0 ? (
        <div className="nx-card p-6 text-sm text-fg-muted">
          Tu rol no tiene permiso para ver postulaciones de productores, artistas ni proveedores.
        </div>
      ) : loading ? (
        <LoadingBlock label="Cargando postulaciones…" />
      ) : (
        <>
          {failedKinds.length > 0 && (
            <div className="nx-card p-3 flex items-center gap-2 text-sm">
              <AlertCircle size={16} className="text-danger shrink-0" />
              <span className="text-fg-muted flex-1">
                No se pudieron cargar: {failedKinds.map((k) => KINDS[k].plural.toLowerCase()).join(", ")}.
              </span>
              <button onClick={refetchAll} className="nx-btn-secondary">Reintentar</button>
            </div>
          )}

          <p className="text-2xs text-fg-subtle tabular-nums">
            {groups.length} {groups.length === 1 ? "persona" : "personas"} · {items.length}{" "}
            {items.length === 1 ? "postulación" : "postulaciones"}
            {multiProfile > 0 && ` · ${multiProfile} con más de un perfil`}
            {truncated && " · se muestran las 100 más recientes por tipo; para ver más, usa la pestaña de cada tipo o busca"}
          </p>

          <div className="nx-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="nx-table">
                <thead>
                  <tr>
                    <th style={{ minWidth: 220 }}>Persona</th>
                    <th>Perfiles</th>
                    <th style={{ width: 1, whiteSpace: "nowrap" }}>Ubicación</th>
                    <th className="text-right" style={{ width: 1, whiteSpace: "nowrap" }}>Última</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center text-fg-subtle py-10">
                        <Inbox size={18} className="inline-block mb-1 opacity-60" />
                        <div>
                          {view === "review"
                            ? "No hay postulaciones por revisar. Todo al día."
                            : "No hay postulaciones que coincidan con los filtros."}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    groups.map((g) => (
                      <tr key={g.key}>
                        <td>
                          <div className="font-medium text-fg">{g.name || "—"}</div>
                          <div className="text-2xs text-fg-subtle break-all">{g.email || "—"}</div>
                        </td>
                        <td>
                          <div className="flex flex-wrap gap-1.5">
                            {g.items.map((it) => {
                              const Icon = KINDS[it.kind].icon;
                              const isSel = selected?.kind === it.kind && selected.id === it.id;
                              return (
                                <button
                                  key={`${it.kind}:${it.id}`}
                                  onClick={() => setSelected({ kind: it.kind, id: it.id })}
                                  className={cn(
                                    "inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs text-left transition-colors",
                                    isSel
                                      ? "border-accent bg-accent-soft/40"
                                      : "border-border hover:border-accent hover:bg-bg-hover",
                                  )}
                                  title={`Abrir postulación #${it.id}`}
                                >
                                  <Icon size={13} className="text-fg-subtle shrink-0" />
                                  <span className="font-medium text-fg">{KINDS[it.kind].label}</span>
                                  <span className="text-fg-muted truncate max-w-[180px]">· {it.title}</span>
                                  <StatusBadge status={it.status} />
                                </button>
                              );
                            })}
                          </div>
                        </td>
                        <td className="text-xs text-fg-muted whitespace-nowrap">
                          {g.items[0]?.location || "—"}
                        </td>
                        <td className="text-right text-xs text-fg-subtle whitespace-nowrap tabular-nums">
                          {formatDate(g.latestAt)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <ProducerDrawer
        selectedId={selectedId("producer")}
        data={producerDetail.data ?? null}
        loading={producerDetail.isLoading && selectedId("producer") !== null}
        onClose={() => setSelected(null)}
      />
      <ArtistDrawer
        selectedId={selectedId("artist")}
        data={artistDetail.data ?? null}
        loading={artistDetail.isLoading && selectedId("artist") !== null}
        onClose={() => setSelected(null)}
      />
      <ProviderDrawer
        selectedId={selectedId("provider")}
        data={providerDetail.data ?? null}
        loading={providerDetail.isLoading && selectedId("provider") !== null}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}

function KindChip({
  active,
  onClick,
  label,
  icon: Icon,
  count,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  icon?: LucideIcon;
  count?: number;
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors",
        active ? "border-accent bg-accent-soft/40 text-fg" : "border-border text-fg-muted hover:text-fg hover:bg-bg-hover",
      )}
    >
      {Icon && <Icon size={13} />}
      {label}
      {typeof count === "number" && <span className="tabular-nums text-fg-subtle">{count}</span>}
    </button>
  );
}
